// V7.9 - IndexedDB Offline Store
// Provides local storage for events, persons, QR codes, images, and agents
// Does NOT alter any existing functionality — purely additive layer

const DB_NAME = 'ella-offline';
const DB_VERSION = 1;

// Store names
const STORES = {
  EVENTS: 'offline_events',
  PERSONS: 'offline_persons',
  QR_CODES: 'offline_qr_codes',
  IMAGES: 'offline_images',
  AGENTS: 'offline_agents',
  OUTBOX: 'offline_outbox',
  SYNC_LOG: 'sync_log',
} as const;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      // Events store
      if (!db.objectStoreNames.contains(STORES.EVENTS)) {
        const eventsStore = db.createObjectStore(STORES.EVENTS, { keyPath: 'localId', autoIncrement: true });
        eventsStore.createIndex('serverId', 'serverId', { unique: false });
        eventsStore.createIndex('status', 'status', { unique: false });
        eventsStore.createIndex('synced', 'synced', { unique: false });
        eventsStore.createIndex('createdAt', 'createdAt', { unique: false });
      }
      // Persons store (cached from server)
      if (!db.objectStoreNames.contains(STORES.PERSONS)) {
        const personsStore = db.createObjectStore(STORES.PERSONS, { keyPath: 'id' });
        personsStore.createIndex('name', 'name', { unique: false });
        personsStore.createIndex('classification', 'classification', { unique: false });
      }
      // QR Codes store (authorized IDs cache)
      if (!db.objectStoreNames.contains(STORES.QR_CODES)) {
        const qrStore = db.createObjectStore(STORES.QR_CODES, { keyPath: 'code' });
        qrStore.createIndex('sector', 'sector', { unique: false });
        qrStore.createIndex('active', 'active', { unique: false });
      }
      // Images store (offline captured blobs)
      if (!db.objectStoreNames.contains(STORES.IMAGES)) {
        const imgStore = db.createObjectStore(STORES.IMAGES, { keyPath: 'localId', autoIncrement: true });
        imgStore.createIndex('eventLocalId', 'eventLocalId', { unique: false });
        imgStore.createIndex('synced', 'synced', { unique: false });
        imgStore.createIndex('type', 'type', { unique: false });
      }
      // Agents store (cached from server)
      if (!db.objectStoreNames.contains(STORES.AGENTS)) {
        const agentsStore = db.createObjectStore(STORES.AGENTS, { keyPath: 'id' });
        agentsStore.createIndex('status', 'status', { unique: false });
      }
      // Outbox store (pending mutations to sync)
      if (!db.objectStoreNames.contains(STORES.OUTBOX)) {
        const outboxStore = db.createObjectStore(STORES.OUTBOX, { keyPath: 'id', autoIncrement: true });
        outboxStore.createIndex('type', 'type', { unique: false });
        outboxStore.createIndex('synced', 'synced', { unique: false });
        outboxStore.createIndex('createdAt', 'createdAt', { unique: false });
      }
      // Sync log store
      if (!db.objectStoreNames.contains(STORES.SYNC_LOG)) {
        const syncStore = db.createObjectStore(STORES.SYNC_LOG, { keyPath: 'id', autoIncrement: true });
        syncStore.createIndex('syncedAt', 'syncedAt', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return dbPromise;
}

// ============================================================================
// Generic CRUD helpers
// ============================================================================

async function getStore(storeName: string, mode: IDBTransactionMode = 'readonly') {
  const db = await openDB();
  const tx = db.transaction(storeName, mode);
  return { store: tx.objectStore(storeName), tx };
}

async function putItem<T>(storeName: string, item: T): Promise<IDBValidKey> {
  const { store, tx } = await getStore(storeName, 'readwrite');
  return new Promise((resolve, reject) => {
    const req = store.put(item);
    req.onsuccess = () => resolve(req.result);
    tx.onerror = () => reject(tx.error);
  });
}

async function getItem<T>(storeName: string, key: IDBValidKey): Promise<T | undefined> {
  const { store } = await getStore(storeName);
  return new Promise((resolve, reject) => {
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error);
  });
}

async function getAllItems<T>(storeName: string): Promise<T[]> {
  const { store } = await getStore(storeName);
  return new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result as T[]);
    req.onerror = () => reject(req.error);
  });
}

async function getByIndex<T>(storeName: string, indexName: string, value: IDBValidKey): Promise<T[]> {
  const { store } = await getStore(storeName);
  const index = store.index(indexName);
  return new Promise((resolve, reject) => {
    const req = index.getAll(value);
    req.onsuccess = () => resolve(req.result as T[]);
    req.onerror = () => reject(req.error);
  });
}

async function deleteItem(storeName: string, key: IDBValidKey): Promise<void> {
  const { store, tx } = await getStore(storeName, 'readwrite');
  return new Promise((resolve, reject) => {
    store.delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function clearStore(storeName: string): Promise<void> {
  const { store, tx } = await getStore(storeName, 'readwrite');
  return new Promise((resolve, reject) => {
    store.clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ============================================================================
// Offline Event types and functions
// ============================================================================

export interface OfflineEvent {
  localId?: number;
  serverId?: number;
  eventType: string;
  classification: string;
  status: string;
  sector: string;
  personName: string;
  person2Name?: string;
  notes?: string;
  synced: boolean;
  createdAt: number; // real timestamp when event occurred
  syncedAt?: number; // timestamp when synced to server
  idempotencyKey: string; // prevent duplicates
}

export interface OfflinePerson {
  id: number;
  name: string;
  classification: string;
  descriptors?: string; // JSON stringified face descriptors
  imageUrl?: string;
  gender?: string;
  cachedAt: number;
}

export interface OfflineQRCode {
  code: string;
  sector: string;
  active: boolean;
  validUntil?: number;
  cachedAt: number;
}

export interface OfflineImage {
  localId?: number;
  eventLocalId: number;
  blob: Blob;
  mimeType: string;
  type: 'photo' | 'video';
  origin: string;
  synced: boolean;
  createdAt: number;
}

export interface OfflineAgent {
  id: number;
  name: string;
  status: string;
  phone?: string;
  sector?: string;
  cachedAt: number;
}

export interface OutboxItem {
  id?: number;
  type: 'event' | 'image' | 'status_update' | 'dispatch';
  payload: string; // JSON stringified
  synced: boolean;
  createdAt: number;
  attempts: number;
  lastAttemptAt?: number;
  idempotencyKey: string;
}

export interface SyncLogEntry {
  id?: number;
  syncedAt: number;
  itemCount: number;
  errors: number;
  details: string;
}

// ============================================================================
// Events
// ============================================================================

export async function saveOfflineEvent(event: Omit<OfflineEvent, 'localId'>): Promise<number> {
  const key = await putItem(STORES.EVENTS, event);
  console.log('[Offline] Event saved locally:', key);
  return key as number;
}

export async function getOfflineEvents(): Promise<OfflineEvent[]> {
  return getAllItems<OfflineEvent>(STORES.EVENTS);
}

export async function getUnsyncedEvents(): Promise<OfflineEvent[]> {
  const all = await getAllItems<OfflineEvent>(STORES.EVENTS);
  return all.filter(e => !e.synced);
}

export async function markEventSynced(localId: number, serverId: number): Promise<void> {
  const event = await getItem<OfflineEvent>(STORES.EVENTS, localId);
  if (event) {
    event.synced = true;
    event.serverId = serverId;
    event.syncedAt = Date.now();
    await putItem(STORES.EVENTS, event);
  }
}

// ============================================================================
// Persons (cache from server)
// ============================================================================

export async function cachePersons(persons: OfflinePerson[]): Promise<void> {
  const { store, tx } = await getStore(STORES.PERSONS, 'readwrite');
  // Clear and re-populate
  store.clear();
  for (const p of persons) {
    store.put({ ...p, cachedAt: Date.now() });
  }
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => {
      console.log('[Offline] Cached', persons.length, 'persons');
      resolve();
    };
    tx.onerror = () => reject(tx.error);
  });
}

export async function getCachedPersons(): Promise<OfflinePerson[]> {
  return getAllItems<OfflinePerson>(STORES.PERSONS);
}

export async function getCachedPersonById(id: number): Promise<OfflinePerson | undefined> {
  return getItem<OfflinePerson>(STORES.PERSONS, id);
}

// ============================================================================
// QR Codes (authorized IDs cache)
// ============================================================================

export async function cacheQRCodes(codes: OfflineQRCode[]): Promise<void> {
  const { store, tx } = await getStore(STORES.QR_CODES, 'readwrite');
  store.clear();
  for (const c of codes) {
    store.put({ ...c, cachedAt: Date.now() });
  }
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => {
      console.log('[Offline] Cached', codes.length, 'QR codes');
      resolve();
    };
    tx.onerror = () => reject(tx.error);
  });
}

export async function validateQRCodeOffline(code: string): Promise<OfflineQRCode | undefined> {
  const qr = await getItem<OfflineQRCode>(STORES.QR_CODES, code);
  if (qr && qr.active) {
    if (qr.validUntil && Date.now() > qr.validUntil) {
      return undefined; // expired
    }
    return qr;
  }
  return undefined;
}

export async function getAllCachedQRCodes(): Promise<OfflineQRCode[]> {
  return getAllItems<OfflineQRCode>(STORES.QR_CODES);
}

// ============================================================================
// Images (offline captured)
// ============================================================================

export async function saveOfflineImage(image: Omit<OfflineImage, 'localId'>): Promise<number> {
  const key = await putItem(STORES.IMAGES, image);
  console.log('[Offline] Image saved locally:', key);
  return key as number;
}

export async function getUnsyncedImages(): Promise<OfflineImage[]> {
  const all = await getAllItems<OfflineImage>(STORES.IMAGES);
  return all.filter(img => !img.synced);
}

export async function markImageSynced(localId: number): Promise<void> {
  const img = await getItem<OfflineImage>(STORES.IMAGES, localId);
  if (img) {
    img.synced = true;
    await putItem(STORES.IMAGES, img);
  }
}

export async function getImagesForEvent(eventLocalId: number): Promise<OfflineImage[]> {
  return getByIndex<OfflineImage>(STORES.IMAGES, 'eventLocalId', eventLocalId);
}

// ============================================================================
// Agents (cache from server)
// ============================================================================

export async function cacheAgents(agents: OfflineAgent[]): Promise<void> {
  const { store, tx } = await getStore(STORES.AGENTS, 'readwrite');
  store.clear();
  for (const a of agents) {
    store.put({ ...a, cachedAt: Date.now() });
  }
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => {
      console.log('[Offline] Cached', agents.length, 'agents');
      resolve();
    };
    tx.onerror = () => reject(tx.error);
  });
}

export async function getCachedAgents(): Promise<OfflineAgent[]> {
  return getAllItems<OfflineAgent>(STORES.AGENTS);
}

export async function updateCachedAgentStatus(id: number, status: string): Promise<void> {
  const agent = await getItem<OfflineAgent>(STORES.AGENTS, id);
  if (agent) {
    agent.status = status;
    await putItem(STORES.AGENTS, agent);
  }
}

// ============================================================================
// Outbox (pending mutations)
// ============================================================================

export async function addToOutbox(item: Omit<OutboxItem, 'id'>): Promise<number> {
  const key = await putItem(STORES.OUTBOX, item);
  console.log('[Offline] Added to outbox:', item.type, key);
  return key as number;
}

export async function getOutboxItems(): Promise<OutboxItem[]> {
  const all = await getAllItems<OutboxItem>(STORES.OUTBOX);
  return all.filter(item => !item.synced);
}

export async function markOutboxSynced(id: number): Promise<void> {
  const item = await getItem<OutboxItem>(STORES.OUTBOX, id);
  if (item) {
    item.synced = true;
    await putItem(STORES.OUTBOX, item);
  }
}

export async function updateOutboxAttempt(id: number): Promise<void> {
  const item = await getItem<OutboxItem>(STORES.OUTBOX, id);
  if (item) {
    item.attempts = (item.attempts || 0) + 1;
    item.lastAttemptAt = Date.now();
    await putItem(STORES.OUTBOX, item);
  }
}

export async function getOutboxCount(): Promise<number> {
  const items = await getOutboxItems();
  return items.length;
}

// ============================================================================
// Sync Log
// ============================================================================

export async function addSyncLog(entry: Omit<SyncLogEntry, 'id'>): Promise<void> {
  await putItem(STORES.SYNC_LOG, entry);
}

export async function getSyncLogs(): Promise<SyncLogEntry[]> {
  return getAllItems<SyncLogEntry>(STORES.SYNC_LOG);
}

// ============================================================================
// Utility: Generate idempotency key
// ============================================================================

export function generateIdempotencyKey(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

// ============================================================================
// Utility: Check if IndexedDB is available
// ============================================================================

export function isIndexedDBAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}
